import { useEffect, useRef, useCallback, useState } from "react";
import { postUserRewrapDeks } from "~/services/gateways/user.gateway";
import { unwrapDekFromUser, wrapDekForUser } from "~/helpers/envelopeCrypto";
import { CRYPTO_SERVICE } from "~/workers/crypto";
import { base64ToUint8Array } from "~/hooks/useEncryption";
import { encryptNgramsDeterministic } from "~/helpers/encryption.helper";

export interface UserRewrapProgress {
    total: number;
    processed: number;
    failed: number;
    isRunning: boolean;
}

interface EncryptedField {
    encrypted: string;
    iv: string;
}

interface WsItem {
    id: string;
    user_wrapped_dek: string;
    user_dek_iv: string;
    item_type: string;
    encrypted_fields?: Record<string, unknown>;
}

interface RewrappedItem {
    id: string;
    new_user_wrapped_dek: string;
    new_user_dek_iv: string;
    item_type: string;
    hmacs?: Record<string, string[]>;
}

function isEncryptedField(val: unknown): val is EncryptedField {
    return (
        val != null &&
        typeof val === "object" &&
        "encrypted" in val &&
        "iv" in val &&
        typeof (val as EncryptedField).encrypted === "string" &&
        typeof (val as EncryptedField).iv === "string"
    );
}

/**
 * Decrypt all encrypted fields of an item using its per-item DEK.
 * Returns a plain object with decrypted string values (only the searchable ones).
 */
async function decryptSearchableFields(
    dek: Uint8Array,
    encryptedFields: Record<string, unknown>,
): Promise<Record<string, unknown>> {
    const result: Record<string, unknown> = {};

    for (const [key, val] of Object.entries(encryptedFields)) {
        if (val == null) continue;

        try {
            if (isEncryptedField(val)) {
                const ciphertext = base64ToUint8Array(val.encrypted);
                const iv = base64ToUint8Array(val.iv);
                const { decrypted } = await CRYPTO_SERVICE.decrypt(dek, iv, ciphertext);
                result[key] = decrypted;
            } else {
                // Non-encrypted field (e.g. plain text or already decrypted) — pass through
                result[key] = val;
            }
        } catch {
            // Skip fields that fail to decrypt — they won't be included in HMAC re-indexing
        }
    }

    return result;
}

/**
 * User-side DEK re-wrapping + HMAC re-indexing via single bidirectional WebSocket.
 *
 * CRITICAL SAFETY DESIGN: No DB writes happen during the WS streaming.
 * The client collects ALL re-wrapped items in memory, then submits them
 * atomically via POST /user-rewrap-deks at the very end.
 *
 * If the user disconnects mid-process:
 * - Nothing was written to DB → old key still works for all items
 * - On next login, must_change_secret_key is still true → process restarts fresh
 * - No partial corruption possible
 *
 * Protocol:
 * 1. Server pushes pages of 100 user_wrapped_deks + encrypted searchable fields
 * 2. Client unwraps with old key, re-wraps with new key
 * 3. Client decrypts searchable fields with DEK, computes new HMACs with new key
 * 4. Client sends { type: "next" } for next page
 * 5. When has_more = false → client POSTs all items (DEKs + HMACs) atomically
 * 6. Client sends { type: "done" } → server closes WS
 */
export function useUserRewrapWs() {
    const [progress, setProgress] = useState<UserRewrapProgress>({
        total: 0, processed: 0, failed: 0, isRunning: false,
    });
    const [isComplete, setIsComplete] = useState(false);

    const wsRef = useRef<WebSocket | null>(null);
    const oldKeyRef = useRef<ArrayBuffer | null>(null);
    const newKeyRef = useRef<ArrayBuffer | null>(null);
    const abortRef = useRef(false);
    // Accumulate all re-wrapped items in memory — submitted atomically at the end
    const accumulatedRef = useRef<RewrappedItem[]>([]);

    /** Re-wrap a page of items in memory and compute new HMACs (no DB writes). */
    const processPage = useCallback(async (items: WsItem[]): Promise<{ ok: number; fail: number }> => {
        const oldKey = oldKeyRef.current;
        const newKey = newKeyRef.current;
        if (!oldKey || !newKey) return { ok: 0, fail: items.length };

        let ok = 0;
        let fail = 0;

        for (const item of items) {
            if (abortRef.current) break;
            try {
                const dek = await unwrapDekFromUser(oldKey, item.user_wrapped_dek, item.user_dek_iv);
                const { user_wrapped_dek, user_dek_iv } = await wrapDekForUser(newKey, dek);

                const rewrapped: RewrappedItem = {
                    id: item.id,
                    new_user_wrapped_dek: user_wrapped_dek,
                    new_user_dek_iv: user_dek_iv,
                    item_type: item.item_type,
                };

                // Compute new HMACs if encrypted searchable fields are available
                if (item.encrypted_fields && Object.keys(item.encrypted_fields).length > 0) {
                    try {
                        const decrypted = await decryptSearchableFields(dek, item.encrypted_fields);

                        if (Object.keys(decrypted).length > 0) {
                            // Build the object for ngram extraction based on item type
                            const ngramInput: Record<string, unknown> = {};

                            if (item.item_type === "folder") {
                                if (decrypted.folder_name) ngramInput.folder_name = decrypted.folder_name;
                            } else if (item.item_type === "report") {
                                if (decrypted.report_name) ngramInput.report_name = decrypted.report_name;
                                if (decrypted.subject_name) ngramInput.subject_name = decrypted.subject_name;
                                if (decrypted.subject_mother_name) ngramInput.subject_mother_name = decrypted.subject_mother_name;
                                if (decrypted.subject_name_related) ngramInput.subject_name_related = decrypted.subject_name_related;
                                if (decrypted.subject_multiple_p_names) ngramInput.subject_multiple_p_names = decrypted.subject_multiple_p_names;
                                if (decrypted.subject_multiple_c_names) ngramInput.subject_multiple_c_names = decrypted.subject_multiple_c_names;
                                if (decrypted.report_search_args) ngramInput.report_search_args = decrypted.report_search_args;
                            }

                            if (Object.keys(ngramInput).length > 0) {
                                rewrapped.hmacs = await encryptNgramsDeterministic(newKey, ngramInput);
                            }
                        }
                    } catch (hmacErr) {
                        // HMAC re-indexing failure is non-fatal — DEK re-wrap still succeeds
                        console.warn(`[useUserRewrapWs] HMAC reindex failed for ${item.item_type} ${item.id}:`, hmacErr);
                    }
                }

                accumulatedRef.current.push(rewrapped);
                ok++;
            } catch (err) {
                console.error(`[useUserRewrapWs] Failed ${item.item_type} ${item.id}:`, err);
                fail++;
            }
        }

        return { ok, fail };
    }, []);

    const sendNext = useCallback(() => {
        wsRef.current?.send(JSON.stringify({ type: "next" }));
    }, []);

    const sendDone = useCallback(() => {
        wsRef.current?.send(JSON.stringify({ type: "done" }));
    }, []);

    /** Submit all accumulated items atomically, then signal done. */
    const submitAll = useCallback(async () => {
        const items = accumulatedRef.current;
        if (items.length === 0) {
            sendDone();
            return true;
        }

        // Submit in chunks of 200 to avoid request body size limits,
        // but all chunks must succeed for the operation to be considered complete.
        const CHUNK = 200;
        for (let i = 0; i < items.length; i += CHUNK) {
            const chunk = items.slice(i, i + CHUNK);
            try {
                await postUserRewrapDeks(chunk);
            } catch (err) {
                console.error("[useUserRewrapWs] Atomic submit failed:", err);
                return false;
            }
        }

        sendDone();
        return true;
    }, [sendDone]);

    const handleMessage = useCallback(async (event: MessageEvent) => {
        try {
            const data = JSON.parse(event.data);

            if (data.type === "error") {
                console.error("[useUserRewrapWs] Server error:", data.message);
                setProgress(prev => ({ ...prev, isRunning: false }));
                return;
            }

            if (data.type === "items") {
                setProgress(prev => ({ ...prev, total: data.total_count }));

                if (data.items.length === 0 && !data.has_more) {
                    // No items to process — submit whatever we have and finish
                    const success = await submitAll();
                    if (success) {
                        setIsComplete(true);
                        setProgress(prev => ({ ...prev, isRunning: false }));
                    }
                    return;
                }

                const { ok, fail } = await processPage(data.items);

                setProgress(prev => ({
                    ...prev,
                    processed: prev.processed + ok,
                    failed: prev.failed + fail,
                }));

                if (data.has_more && !abortRef.current) {
                    sendNext();
                } else {
                    // All pages received — submit atomically
                    const success = await submitAll();
                    if (success) {
                        setIsComplete(true);
                        setProgress(prev => ({ ...prev, isRunning: false }));
                    } else {
                        setProgress(prev => ({ ...prev, isRunning: false }));
                    }
                }
            }

            if (data.type === "complete") {
                // Server acknowledged our "done" — WS will close
                setProgress(prev => ({ ...prev, isRunning: false }));
                setIsComplete(true);
            }
        } catch {
            console.warn("[useUserRewrapWs] Failed to parse WS message");
        }
    }, [processPage, sendNext, submitAll]);

    /** Start the re-wrapping flow. Opens the WS and begins processing. */
    const start = useCallback((userId: string, oldKey: ArrayBuffer, newKey: ArrayBuffer) => {
        abortRef.current = false;
        oldKeyRef.current = oldKey;
        newKeyRef.current = newKey;
        accumulatedRef.current = [];
        setProgress({ total: 0, processed: 0, failed: 0, isRunning: true });
        setIsComplete(false);

        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const isDev = import.meta.env.MODE === "development";
        const baseUrl = isDev
            ? import.meta.env.VITE_REPORTS_API_URL?.replace(/^https?:/, protocol) ?? `${protocol}//localhost:8000`
            : `${protocol}//${window.location.host}`;
        const wsUrl = `${baseUrl}/reports/ws/user-rewrap/${userId}`;

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onmessage = handleMessage;
        ws.onclose = () => {
            wsRef.current = null;
        };
        ws.onerror = () => {
            console.error("[useUserRewrapWs] WebSocket error");
        };
    }, [handleMessage]);

    const abort = useCallback(() => {
        abortRef.current = true;
        accumulatedRef.current = []; // Discard accumulated items — no partial writes
        wsRef.current?.close(1000);
        setProgress(prev => ({ ...prev, isRunning: false }));
    }, []);

    useEffect(() => {
        return () => { wsRef.current?.close(1000); };
    }, []);

    return { start, abort, progress, isComplete };
}
